/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { NOT_APPLICABLE } from '@config/constants/common';
import { TSite } from '@global-types/index';
import { apiClient } from '@lib/api-client';
import { QueryConfig } from '@lib/react-query';

import {
	queryOptions,
	useQuery,
} from '@tanstack/react-query';
import { getCountryName } from '@utils/countries';
import {
	formatNumberWithSpaces,
	formatTokenAvailable,
} from '@utils/formats';
import {
	convertGCO2EToTCO2EWithoutUnit,
	formatDate,
} from '@utils/functions';
import { getTechnologyProject } from '@utils/project-technologies';
import { getUnitLabel } from '@utils/units';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
type UseMeasurabilityProjectOptions = {
	projectId: string;
	queryConfig?: QueryConfig<
		typeof getMeasurabilityProjectQueryOptions
	>;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */

export const getPublicMeasurabilityProjectById = (
	projectId: string
): Promise<any> => {
	return apiClient.get(
		`/public/measurabilities/${projectId}`,
		{
			params: {
				populate:
					'organization, entity, organization.measurabilities,organization.logo, organization.entities, organization.entities.projects, cover, logo, publicSpecs, internalSpecs, supportedBy.logo',
			},
		}
	);
};

export const getMeasurabilityProjectQueryOptions = (
	projectId: string
) => {
	return queryOptions({
		queryKey: [
			'measurability-projects-public',
			projectId,
		],
		queryFn: () => {
			return getPublicMeasurabilityProjectById(projectId);
		},
		staleTime: 1000,
	});
};

export const getUserById = (
	userId: string
): Promise<any> => {
	return apiClient.get(`/users/${userId}`);
};

export const getUserQueryOptions = (userId: string) => {
	return queryOptions({
		queryKey: ['user-id', userId],
		queryFn: () => getUserById(userId),
	});
};

export const getOrganizationById = (
	organizationId: string
): Promise<any> => {
	return apiClient.get(
		`/organizations/${organizationId}`
	);
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
export const useMeasurabilityProject = ({
	projectId,
	queryConfig,
}: UseMeasurabilityProjectOptions) => {
	const { t } = useTranslation();

	const {
		data,
		isFetching,
		isFetched,
		isRefetching,
		isError,
		isLoading,
	} = useQuery({
		...getMeasurabilityProjectQueryOptions(projectId),
		...queryConfig,
	});

	let countryName = '';
	let technolgyProject: any = '';
	let sellPrice = '';
	let cover = '';

	let sites = [];

	if (data && isFetched && !isError) {
		if (data.technology) {
			technolgyProject = getTechnologyProject(
				data.technology
			);
			cover =
				data && data.cover
					? data.cover
					: technolgyProject
						? technolgyProject.image
						: '';
		}

		if (data.location && data.location.countryCode)
			countryName = getCountryName(
				data.location.countryCode
			);

		if (!isNaN(data.sellPrice_europertco2eq))
			sellPrice = `${formatNumberWithSpaces(data.sellPrice_europertco2eq)} ${t('global.sellPriceUnit')}`;

		if (
			data.organization &&
			data.organization.measurabilities &&
			Array.isArray(data.organization.measurabilities) &&
			data.organization.measurabilities.find(
				(item: TSite) => item.id == projectId
			)
		) {
			const foundProject = data.organization
				.measurabilities
				? data.organization.measurabilities.find(
						(item: TSite) => item.id == projectId
					)
				: [];

			sites = [];
		}
	}

	const { data: user } = useQuery({
		queryKey: [
			'user',
			data &&
				data.organization &&
				data.organization.owner,
		],
		queryFn: () => getUserById(data.organization.owner),
		enabled: false,
	});

	const { data: contact } = useQuery({
		queryKey: [
			'contact',
			data && data.entity && data.entity.contact,
		],
		queryFn: () => getUserById(data.entity.contact),
		enabled: false,
	});

	return {
		data: {
			...data,
			country: {
				name: countryName,
				code: data?.location?.countryCode.toLowerCase(),
				place: data?.location?.place,
			},
			technology: technolgyProject.label
				? technolgyProject.label
				: NOT_APPLICABLE,
			sellPrice,
			cover,
			supportedBy: data?.supportedBy?.logo,
			availableStock: `${
				data?.quantity?.remaining_aco2
					? formatNumberWithSpaces(
							Number(
								formatTokenAvailable(
									convertGCO2EToTCO2EWithoutUnit(
										data.quantity.remaining_aco2
									)
								)
							)
						)
					: 0
			} ${getUnitLabel('tco2eq')}`,
		},
		sites: [],
		user: {},
		contactProducer: {},
		isFetching,
		isFetched,
		isRefetching,
		isError,
		isLoading,
	};
};
